import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Linkedin, MapPin, Upload, ArrowRight, Loader2, Download } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useSeo } from "@/hooks/useSeo";
import { IMAGES, CONTACT, BUSINESS_VERTICALS, COMPANY_PROFILE_PDF } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", company: "", designation: "", mobile: "", email: "",
  project_location: "", requirement: "", business_vertical: "",
  estimated_project_size: "", expected_start: "",
};

export default function Contact() {
  useSeo({
    title: "Contact AutoMex | Discuss Your Industrial Project in Pune",
    description: "Contact AutoMex Group to plan, build, expand or automate your industrial facility. Call +91 70586 28066 or send your drawing / BOQ / RFQ. Pune, Maharashtra.",
    path: "/contact",
  });

  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_vertical) {
      toast.error("Please select a business vertical.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("attachment", file);
      await axios.post(`${API}/leads`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thank you — your enquiry has been received. Our team will get back to you shortly.");
      setForm(initial);
      setFile(null);
      e.target.reset();
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us directly.");
    } finally {
      setLoading(false);
    }
  };

  const channels = [
    { icon: Phone, label: "Call", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}`, testid: "contact-call" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phone, href: `https://wa.me/${CONTACT.whatsapp}`, testid: "contact-whatsapp" },
    { icon: Linkedin, label: "LinkedIn", value: "AutoMex Group India", href: CONTACT.linkedin, testid: "contact-linkedin" },
  ];

  return (
    <>
      <PageHero
        overline="Contact"
        title="Planning Your Next Industrial Project?"
        subtitle="Tell us what you are building, expanding or automating."
        image={IMAGES.crane}
        crumb="Contact"
      />

      <section className="py-24 md:py-32 bg-automex-mist bg-blueprint">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Contact channels */}
          <div className="lg:col-span-4">
            <Reveal>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-automex-ink">Reach AutoMex Group</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">Speak with our team about your facility, expansion or automation requirement.</p>
              <a
                href={COMPANY_PROFILE_PDF.url}
                download={COMPANY_PROFILE_PDF.filename}
                data-testid="contact-profile-cta"
                className="group mt-6 inline-flex items-center gap-2.5 border-2 border-automex-navy px-6 py-3 text-sm font-bold uppercase tracking-wider text-automex-navy rounded-sm hover:bg-automex-navy hover:text-white transition-colors"
              >
                <Download className="h-4 w-4" />
                Download Company Profile
              </a>
              <div className="mt-8 space-y-3">
                {channels.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={c.testid}
                    className="group flex items-center gap-4 bg-white border border-slate-200 rounded-sm p-4 transition-colors hover:border-automex-teal"
                  >
                    <span className="flex h-11 w-11 items-center justify-center bg-automex-navy text-automex-teal rounded-sm shrink-0"><c.icon className="h-5 w-5" /></span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">{c.label}</span>
                      <span className="block truncate text-sm font-semibold text-automex-ink">{c.value}</span>
                    </span>
                  </a>
                ))}
                <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-sm p-4">
                  <span className="flex h-11 w-11 items-center justify-center bg-automex-navy text-automex-teal rounded-sm shrink-0"><MapPin className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-automex-blue">Location</span>
                    <span className="block text-sm font-semibold text-automex-ink">{CONTACT.location}</span>
                  </span>
                </div>
                <div className="overflow-hidden rounded-sm border border-slate-200" data-testid="contact-map">
                  <iframe
                    title="AutoMex Group location — Pune, Maharashtra, India"
                    src="https://www.google.com/maps?q=Pune%2C%20Maharashtra%2C%20India&output=embed"
                    className="w-full h-56"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <form onSubmit={onSubmit} data-testid="contact-form" className="bg-white border border-slate-200 rounded-sm p-6 sm:p-10">
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Name" required><Input required value={form.name} onChange={set("name")} data-testid="input-name" placeholder="Your full name" /></Field>
                  <Field label="Company" required><Input required value={form.company} onChange={set("company")} data-testid="input-company" placeholder="Company name" /></Field>
                  <Field label="Designation"><Input value={form.designation} onChange={set("designation")} data-testid="input-designation" placeholder="e.g. Plant Head" /></Field>
                  <Field label="Mobile" required><Input required type="tel" value={form.mobile} onChange={set("mobile")} data-testid="input-mobile" placeholder="+91 ..." /></Field>
                  <Field label="Email" required><Input required type="email" value={form.email} onChange={set("email")} data-testid="input-email" placeholder="you@company.com" /></Field>
                  <Field label="Project Location" required><Input required value={form.project_location} onChange={set("project_location")} data-testid="input-location" placeholder="City / State" /></Field>

                  <Field label="Business Vertical" required>
                    <Select value={form.business_vertical} onValueChange={(v) => setForm((f) => ({ ...f, business_vertical: v }))}>
                      <SelectTrigger data-testid="select-vertical" className="rounded-sm"><SelectValue placeholder="Select vertical" /></SelectTrigger>
                      <SelectContent>
                        {BUSINESS_VERTICALS.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Estimated Project Size"><Input value={form.estimated_project_size} onChange={set("estimated_project_size")} data-testid="input-size" placeholder="e.g. 50,000 sq ft / range" /></Field>
                  <Field label="Expected Start"><Input value={form.expected_start} onChange={set("expected_start")} data-testid="input-start" placeholder="e.g. Q3 2026" /></Field>

                  <div className="sm:col-span-1">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Upload Drawing / BOQ / RFQ</Label>
                    <label className="mt-2 flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-slate-300 px-4 py-2.5 text-sm text-slate-500 hover:border-automex-teal">
                      <Upload className="h-4 w-4 shrink-0" />
                      <span className="truncate">{file ? file.name : "Choose file (max 10MB)"}</span>
                      <input type="file" className="hidden" data-testid="input-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </label>
                  </div>
                </div>

                <div className="mt-6">
                  <Field label="Requirement" required>
                    <Textarea required rows={4} value={form.requirement} onChange={set("requirement")} data-testid="input-requirement" placeholder="Describe what you are building, expanding or automating." />
                  </Field>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileTap={{ scale: 0.98 }}
                  data-testid="submit-lead"
                  className="mt-8 group inline-flex items-center justify-center gap-2.5 bg-automex-navy text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-automex-ink transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? "Sending..." : "Discuss Your Requirement"}
                  {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {label}{required && <span className="text-automex-teal"> *</span>}
      </Label>
      <div className="mt-2 [&_input]:rounded-sm [&_textarea]:rounded-sm">{children}</div>
    </div>
  );
}
